import { DEFAULT_HOME_BLOCK_IDS, HomeBlockId } from './home-blocks';

/** O que decide se cada bloco da Home existe para a pessoa e se aparece agora (as mesmas regras de antes do card 3788). */
export interface HomeBlockConditions {
  /** Feature flag `dashboard-economy-scenario` (Economia Prevista e Performance da Dívida). */
  economyScenarioEnabled: boolean;
  /** Ao menos uma empresa escolhida no seletor (o cenário de economia não aparece com "Todas empresas"). */
  hasCompanySelection: boolean;
  /** Exatamente uma empresa escolhida (Score de Crédito). */
  hasSingleCompany: boolean;
  /** `CREDITSCORE_VIEW_COMPANY`. */
  canViewCreditScore: boolean;
  /** `BANKRELATIONSHIP_LIST`. */
  canViewBankLimits: boolean;
  /** `MEETING_VIEW_COMPANY` ou `MEETING_LIST`. */
  canViewMeetings: boolean;
}

/**
 * Blocos que existem para a pessoa (flag e permissões), na ordem padrão: são os que aparecem na gaveta Personalizar.
 * A escolha de empresa não entra aqui: o bloco continua na gaveta e só não renderiza enquanto a empresa não serve.
 * @param conditions Flag e permissões.
 */
export function availableHomeBlocks(conditions: HomeBlockConditions): HomeBlockId[] {
  return DEFAULT_HOME_BLOCK_IDS.filter(id => {
    switch (id) {
      case 'economy':
      case 'performance':
        return conditions.economyScenarioEnabled;
      case 'credit-score':
        return conditions.canViewCreditScore;
      case 'bank-limits':
        return conditions.canViewBankLimits;
      case 'meetings':
        return conditions.canViewMeetings;
      default:
        return true;
    }
  });
}

/**
 * Dos blocos ligados, os que renderizam agora (inclui a regra de empresa): é sobre eles que as linhas são montadas,
 * para um bloco que não aparece não deixar meia linha vazia.
 * @param visibleIds Blocos ligados, na ordem da pessoa.
 * @param conditions Flag, permissões e empresa escolhida.
 */
export function renderableHomeBlocks(
  visibleIds: readonly HomeBlockId[],
  conditions: HomeBlockConditions
): HomeBlockId[] {
  const available = new Set(availableHomeBlocks(conditions));
  return visibleIds.filter(id => {
    if (!available.has(id)) {
      return false;
    }
    if (id === 'economy' || id === 'performance') {
      return conditions.hasCompanySelection;
    }
    if (id === 'credit-score') {
      return conditions.hasSingleCompany;
    }
    return true;
  });
}

/** Dica na gaveta para o bloco que não aparece com a empresa escolhida agora. */
export type HomeSelectionHints = Partial<Record<HomeBlockId, string>>;

/** Score de Crédito: só com uma empresa. */
export const SINGLE_COMPANY_HINT = 'Aparece com uma empresa escolhida';

/** Economia Prevista e Performance da Dívida: não aparecem com "Todas empresas". */
export const COMPANY_SELECTION_HINT = 'Aparece com empresa escolhida';

/**
 * Dicas da gaveta Personalizar (crítica G1 do card 3788): o bloco existe para a pessoa, mas a escolha de empresa de agora
 * não o mostra. Sem dica para bloco que não está disponível (ele nem está na gaveta).
 * @param conditions Flag, permissões e empresa escolhida.
 */
export function homeSelectionHints(conditions: HomeBlockConditions): HomeSelectionHints {
  const available = new Set(availableHomeBlocks(conditions));
  const hints: HomeSelectionHints = {};
  if (!conditions.hasCompanySelection) {
    for (const id of ['economy', 'performance'] as const) {
      if (available.has(id)) hints[id] = COMPANY_SELECTION_HINT;
    }
  }
  if (!conditions.hasSingleCompany && available.has('credit-score')) {
    hints['credit-score'] = SINGLE_COMPANY_HINT;
  }
  return hints;
}
